import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCourse, useCourses, useGenerateScenario } from "../api/queries";
import { addSimJob, dismissSimJob, useSimJobs } from "../api/simJobs";
import type { Difficulty } from "../api/types";
import { ActionErrors } from "../components/SimPatient";
import { SimJobProgress } from "../components/SimJobs";
import { Button, Card, PageHeader } from "../components/ui";

const input = "w-full rounded border px-2 py-1.5 text-sm";
const DEFAULT_LEARNERS = "Midwives, Nurses, Health Officers";

// Course and module are the author's choice; everything else in the AI
// Simulated Patient Case Template is written by the LLM (in the Celery
// worker) from the course's approved content, then reviewed in the editor.
export function SimPatientGeneratePage() {
  const navigate = useNavigate();
  const courses = useCourses();
  const generate = useGenerateScenario();
  const [courseId, setCourseId] = useState<number | null>(null);
  const [moduleCmid, setModuleCmid] = useState<number | null>(null);
  const [difficulty, setDifficulty] = useState<Difficulty | "">("");
  const [learners, setLearners] = useState(DEFAULT_LEARNERS);
  const [focus, setFocus] = useState("");
  const course = useCourse(courseId ?? 0, courseId !== null);
  const modules = course.data?.modules.filter((m) => !m.deleted_at) ?? [];

  // The generation this page started; it keeps running if you leave.
  const jobs = useSimJobs();
  // Coming back to this page picks up a generation still running (or that failed).
  const [taskId, setTaskId] = useState<string | null>(
    () => [...jobs].reverse().find((j) => j.kind === "generate" && !j.outcome?.ok)?.taskId ?? null
  );
  const job = jobs.find((j) => j.taskId === taskId) ?? null;
  const running = generate.isPending || (job !== null && !job.outcome);

  useEffect(() => {
    if (job?.outcome?.ok && job.scenarioId !== null) {
      dismissSimJob(job.taskId);
      navigate(`/sim-patients/${job.scenarioId}/edit`, { state: { generated: true } });
    }
  }, [job, navigate]);

  const submit = () => {
    if (courseId === null) return;
    const courseName = courses.data?.courses.find((c) => c.moodle_id === courseId)?.fullname ?? `Course ${courseId}`;
    const moduleName = modules.find((m) => m.moodle_cmid === moduleCmid)?.name;
    if (job) dismissSimJob(job.taskId);
    generate.mutate(
      {
        course_moodle_id: courseId,
        module_cmid: moduleCmid,
        difficulty: difficulty || null,
        target_learners: learners.split(",").map((s) => s.trim()).filter(Boolean),
        focus: focus.trim() || null,
      },
      {
        onSuccess: (res) => {
          if (res.mode === "inline") {
            navigate(`/sim-patients/${res.scenario.id}/edit`, { state: { generated: true } });
            return;
          }
          addSimJob({
            taskId: res.task_id,
            kind: "generate",
            label: moduleName ? `${courseName} · ${moduleName}` : courseName,
            scenarioId: null,
            section: null,
          });
          setTaskId(res.task_id);
        },
      }
    );
  };

  return (
    <div className="p-6">
      <div className="mb-2 text-sm"><Link to="/sim-patients" className="text-blue-700 hover:underline">← Simulated patients</Link></div>
      <PageHeader
        title="Generate a simulated patient case"
        subtitle="Pick the course content to build from. The AI writes the full case template from that course's approved material; you review and edit it before publishing."
      />
      <Card className="max-w-2xl">
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          <fieldset disabled={running} className="space-y-4">
            <label className="block">
              <span className="text-xs font-medium text-gray-700">Course</span>
              <select
                required
                className={`${input} mt-1`}
                value={courseId ?? ""}
                onChange={(e) => {
                  setCourseId(e.target.value ? Number(e.target.value) : null);
                  setModuleCmid(null);
                }}
              >
                <option value="">Choose a course…</option>
                {courses.data?.courses.map((c) => <option key={c.moodle_id} value={c.moodle_id}>{c.fullname}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-xs font-medium text-gray-700">Module (optional)</span>
              <select
                className={`${input} mt-1`}
                disabled={!courseId}
                value={moduleCmid ?? ""}
                onChange={(e) => setModuleCmid(e.target.value ? Number(e.target.value) : null)}
              >
                <option value="">Whole course</option>
                {modules.map((m) => <option key={m.moodle_cmid} value={m.moodle_cmid}>{m.section_name ? `${m.section_name} · ` : ""}{m.name}</option>)}
              </select>
              <span className="mt-0.5 block text-xs text-gray-500">For large courses, picking a module keeps the case focused.</span>
            </label>

            <details className="rounded border px-3 py-2">
              <summary className="cursor-pointer text-sm text-gray-700">Guide the AI (optional)</summary>
              <div className="mt-3 space-y-3">
                <label className="block">
                  <span className="text-xs font-medium text-gray-700">Clinical focus</span>
                  <input className={`${input} mt-1`} value={focus} onChange={(e) => setFocus(e.target.value)} placeholder="e.g. preeclampsia with severe features" maxLength={500} />
                </label>
                <div className="grid gap-3 sm:grid-cols-2">
                  <label className="block">
                    <span className="text-xs font-medium text-gray-700">Difficulty</span>
                    <select className={`${input} mt-1`} value={difficulty} onChange={(e) => setDifficulty(e.target.value as Difficulty | "")}>
                      <option value="">Let the AI choose</option>
                      <option value="beginner">Beginner</option>
                      <option value="intermediate">Intermediate</option>
                      <option value="advanced">Advanced</option>
                    </select>
                  </label>
                  <label className="block">
                    <span className="text-xs font-medium text-gray-700">Target learners</span>
                    <input className={`${input} mt-1`} value={learners} onChange={(e) => setLearners(e.target.value)} />
                  </label>
                </div>
              </div>
            </details>

            <Button type="submit" variant="primary" disabled={courseId === null} loading={generate.isPending}>
              {job?.outcome && !job.outcome.ok ? "Try again" : "Generate case"}
            </Button>
          </fieldset>
          {generate.error && <ActionErrors error={generate.error} />}
        </form>

        {job && (
          <div className="mt-5 border-t pt-4">
            <SimJobProgress job={job} />
            {!job.outcome && (
              <p className="mt-3 text-xs text-gray-500">
                Running on the worker. Usually 1–3 minutes. You can leave this page; progress follows you in the corner and the case opens from there when it's ready.
              </p>
            )}
          </div>
        )}
      </Card>
    </div>
  );
}
